import { ChevronDown, ChevronRight, Download, Plus, Upload } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { RecordListPage, type RecordListConfig } from '../list/RecordListPage'
import type { OrganisationRow } from '../query/types'
import { MemberRows } from './MemberRows'
import { DuplicatesBanner } from './merge/DuplicatesBanner'
import { NewOrganisationSheet } from './NewOrganisationSheet'
import { OrganisationCardList } from './OrganisationCardList'
import { organisationColumns } from './organisationColumns'

const noun = (count: number): string => (count === 1 ? '1 organisatie' : `${count} organisaties`)

const SORT_OPTIONS = [
  { value: 'name', label: 'Naam (A–Z)' },
  { value: '-last_activity_at', label: 'Laatst gewijzigd (nieuwste eerst)' },
  { value: 'last_contact_at', label: 'Laatste contact (oudste eerst)' },
] as const

/**
 * Organisaties (spec-63 E1, rebuilt in E2): the one list page with the views, filters, columns, bulk
 * and export of the register of organisations, with the contact persons expandable under each row
 * and a "+ Organisatie" sheet.
 */
export function OrganisationsPage() {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [sheetOpen, setSheetOpen] = useState(false)

  function toggle(id: string) {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const newOrganisation = (
    <Button type="button" aria-label="Nieuwe organisatie" onClick={() => setSheetOpen(true)}>
      <Plus />
      Organisatie
    </Button>
  )

  const config: RecordListConfig<OrganisationRow> = {
    object: 'organisaties',
    title: 'Organisaties',
    subtitle: 'Organisaties met hun contactpersonen',
    searchPlaceholder: 'Zoek op naam, website, plaats of KvK…',
    searchLabel: 'Zoek organisaties',
    noun,
    rowKey: (row) => row.id,
    rowLabel: (row) => row.name,
    columns: organisationColumns(),
    cardList: (rows, sort) => <OrganisationCardList rows={rows} sort={sort} />,
    sortOptions: SORT_OPTIONS,
    leadingColumn: {
      key: 'toggle',
      header: <span className="sr-only">Uitklappen</span>,
      className: 'w-8',
      cell: (row) => (
        <button
          type="button"
          aria-expanded={expanded.has(row.id)}
          aria-label={`Contactpersonen van ${row.name}`}
          onClick={() => toggle(row.id)}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {expanded.has(row.id) ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
        </button>
      ),
    },
    toolbar: (rows) => {
      const allOpen = rows.length > 0 && rows.every((row) => expanded.has(row.id))
      return (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={rows.length === 0}
          onClick={() => setExpanded(allOpen ? new Set() : new Set(rows.map((row) => row.id)))}
        >
          {allOpen ? 'Alles inklappen' : 'Alles uitklappen'}
        </Button>
      )
    },
    renderAfterRow: (row, colSpan) =>
      expanded.has(row.id) ? <MemberRows key={`${row.id}-leden`} organisation={row} colSpan={colSpan} /> : null,
    headerActions: (list, { exporting, exportRows }) => (
      <>
        <DuplicatesBanner onMerged={() => list.reload()} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" disabled={exporting || !list.query}>
              <Download />
              {exporting ? 'Bezig…' : 'Exporteren'}
              <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => void exportRows()}>Organisaties</DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => void exportRows({ variant: 'met_contactpersonen', filename: 'organisaties-met-contactpersonen.xlsx' })}
            >
              Organisaties met contactpersonen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button type="button" variant="outline" onClick={() => navigate('/relaties/importeren?modus=organisaties')}>
          <Upload />
          Importeren
        </Button>
        {newOrganisation}
      </>
    ),
    empty: <EmptyState title="Nog geen organisaties." description="Leg de eerste organisatie vast." action={newOrganisation} />,
    exportFilename: 'organisaties.xlsx',
    dialogs: () => (
      <NewOrganisationSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onCreated={(organisation) => {
          setSheetOpen(false)
          navigate(`/relaties/organisaties/${organisation.id}?tab=contactpersonen`)
        }}
      />
    ),
  }

  return <RecordListPage config={config} />
}
